import { createContext, useContext } from "react";
import type { components } from "@cntrl/api-client";

type NavigationDocument = components["schemas"]["NavigationDocument"];

/**
 * The OrgContext — the roster/org-shell analogue of BandContext. The minimal org shell
 * mounts its team + settings modules under this; org-scoped hooks read `orgId` from here (or take it
 * explicitly, like useProfile). The value comes FROM THE SERVER navigation document (`org:{orgId}` context) —
 * the client never computes an effective org role. The full RosterShell + dashboards are Phase 7; this carries
 * only what the invite/settings surfaces need.
 */
export interface OrgContextValue {
  orgId: string;
  /** The server-computed effective role on this org seat — never client-derived.*/
  role: string;
  /** Held capability tokens `{token: true}` — the per-screen flags the client keys on.*/
  capabilities: Readonly<Record<string, boolean>>;
}

const OrgContext = createContext<OrgContextValue | null>(null);

export const OrgProvider = OrgContext.Provider;

/** The active OrgContext, or null when no org is selected. */
export function useOrgContextOrNull(): OrgContextValue | null {
  return useContext(OrgContext);
}

/** The active OrgContext; throws if an org-scoped module mounts without one (a routing bug). */
export function useOrgContext(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrgContext() requires a mounted <OrgProvider>.");
  return ctx;
}

/** True iff the caller holds the org capability token (capabilities-as-data — never a client role matrix). */
export function useOrgCapability(token: string): boolean {
  const ctx = useContext(OrgContext);
  return ctx?.capabilities[token] === true;
}

/** Build an OrgContextValue from the server navigation document (`org:{orgId}` context). */
export function orgContextFromNav(nav: NavigationDocument): OrgContextValue | null {
  const orgId = nav.context?.orgId;
  if (!orgId) return null;
  return {
    orgId,
    role: nav.effectiveRole ?? "",
    capabilities: (nav.capabilities as Record<string, boolean> | undefined) ?? {},
  };
}
